// Avoid re-exports in favor of direct imports or entry-point files
/* eslint-disable no-barrel-files/no-barrel-files */

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { LoadablePaint, LoadableAfterPaint, type LoadableType } from '@confluence/loadable';

import type { LandingPageProps } from './landing-page/LandingPage.tsx';
import type { SpaceSettingsNavigationProps } from './navigation/SpaceSettingsNavigation.tsx';
import type { SpaceSettingsSideNavigationProps } from './side-navigation/SpaceSettingsSideNavigation.tsx';
import type { SpaceSettingsSidebarLinksProps } from './sidebar/SpaceSettingsSidebarLinks.tsx';

export const SpaceSettingsNavigationV2Portal: LoadableType<SpaceSettingsNavigationProps> =
	LoadablePaint({
		loader: async () =>
			(
				await import(
					/* webpackChunkName: "loadable-navigationSpaceSettingsNavigationPortal" */ './navigation/SpaceSettingsNavigationPortal.tsx'
				)
			).SpaceSettingsNavigationPortal,
		name: 'SpaceSettingsNavigationPortal',
	});

export const SpaceSettingsSidebarLinks: LoadableType<SpaceSettingsSidebarLinksProps> =
	LoadableAfterPaint({
		loader: async () =>
			(
				await import(
					/* webpackChunkName: "loadable-sidebarSpaceSettingsSidebarLinks" */ './sidebar/SpaceSettingsSidebarLinks.tsx'
				)
			).SpaceSettingsSidebarLinks,
		name: 'SpaceSettingsSidebarLinks',
	});

export const SpaceSettingsV2ForgeApp: LoadableType<{
	spaceKey: string;
	appId: string;
	envId: string;
	forgeManifestRoute: string;
}> = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-forge-appsForgeApp" */ './forge-apps/ForgeApp.tsx'
			)
		).ForgeApp,
	name: 'SpaceSettingsV2ForgeApp',
});

export const SpaceSettingsV2LandingPage: LoadableType<LandingPageProps> = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-landing-pageLandingPage" */ './landing-page/LandingPage.tsx'
			)
		).LandingPage,
	name: 'SpaceSettingsV2LandingPage',
});

export const AvatarsSettings: LoadableType<{
	spaceKey: string;
}> = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-pagesAvatarsSettings" */ './pages/AvatarsSettings.tsx'
			)
		).AvatarsSettings,
	name: 'AvatarsSettings',
});

export const ContentStateSettings: LoadableType<{
	spaceKey: string;
}> = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-pagesContentStateSettings" */ './pages/ContentStateSettings.tsx'
			)
		).ContentStateSettings,
	name: 'ContentStateSettings',
});

export const ArchiveSpace: LoadableType<{
	spaceKey: string;
}> = LoadablePaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-pagesArchiveSpace" */ './pages/ArchiveSpace.tsx'))
			.ArchiveSpace,
	name: 'ArchiveSpace',
});

export const DataClassificationSpaceSettings: LoadableType<{
	spaceKey: string;
}> = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-dataClassificationSpaceSettings" */ './pages/DataClassificationSpaceSettings.tsx'
			)
		).DataClassificationSpaceSettings,
	name: 'DataClassificationSpaceSettings',
});

export const WatermarkConfigurationSpaceSettings: LoadableType<
	Omit<
		{
			spaceKey: string;
			flags: FlagsStateContainer;
		},
		'flags'
	>
> = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-watermarkConfigurationSpaceSettings" */ './pages/WatermarkConfigurationSpaceSettings.tsx'
			)
		).WatermarkConfigurationSpaceSettings,
	name: 'WatermarkConfigurationSpaceSettings',
});

export const ExportSpaceUnavailable: LoadableType<{
	spaceKey: string;
}> = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-exportSpaceUnavailable" */ './pages/ExportSpaceUnavailable.tsx'
			)
		).ExportSpaceUnavailable,
	name: 'ExportSpaceUnavailable',
});

export const ExportSpace: LoadableType<{
	spaceKey: string;
}> = LoadablePaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-pagesPDFExport" */ './pages/ExportSpace.tsx'))
			.ExportSpace,
	name: 'ExportSpace',
});

export const StatusAndApprovals: LoadableType<{
	spaceKey: string;
}> = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-pagesStatusAndApprovals" */ './pages/StatusAndApprovals.tsx'
			)
		).StatusAndApprovals,
	name: 'StatusAndApprovals',
});

export const LegacyContent: LoadableType<{
	spaceKey: string;
}> = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-pagesPDFExport" */ './pages/legacy-content/LegacyContent.tsx'
			)
		).LegacyContent,
	name: 'LegacyContent',
});

export const EditFeatures: LoadableType<{
	spaceKey: string;
}> = LoadablePaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-pagesEditFeatures" */ './pages/EditFeatures.tsx'))
			.EditFeatures,
	name: 'EditFeatures',
});

export const LazyPdfExportPage: LoadableType<{
	spaceKey: string;
	spaceAri?: string;
	routeOverrideEnabled?: boolean | null;
	handleRefreshSelectedPDFStylingMethod?: () => Promise<void>;
}> = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-PdfExportPage" */ './pages/look-and-feel/pdf-export/PdfExportPage.tsx'
			)
		).PdfExportPage,
	name: 'PdfExportPage',
});

export const LazyNcsPdfStylingPage: LoadableType<{
	spaceKey: string;
	spaceAri?: string;
	routeOverrideEnabled?: boolean | null;
	handleRefreshSelectedPDFStylingMethod?: () => Promise<void>;
}> = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-PdfExportPage" */ './pages/look-and-feel/pdf-export/PdfExportPage.tsx'
			)
		).PdfExportPage,
	name: 'PdfExportPage',
});

export const NcsPdfStylingPage: LoadableType<{
	spaceKey: string;
	spaceAri?: string;
	routeOverrideEnabled?: boolean | null;
	handleRefreshSelectedPDFStylingMethod?: () => Promise<void>;
}> = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-PageLayoutPage" */ './pages/look-and-feel/no-code-styling/NcsPdfStylingPage.tsx'
			)
		).NcsPdfStylingPage,
	name: 'NcsPdfStylingPage',
});

export const LazyPageLayoutPage: LoadableType<{
	spaceKey: string;
}> = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-PageLayoutPage" */ './pages/look-and-feel/page-layout/PageLayoutPage.tsx'
			)
		).PageLayoutPage,
	name: 'PageLayoutPage',
});

export const LazyThemesPage: LoadableType<{
	spaceKey: any;
}> = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ThemesPage" */ './pages/look-and-feel/themes/ThemesPage.tsx'
			)
		).ThemesPage,
	name: 'ThemesPage',
});

export const LazyTemplatesPage: LoadableType<{
	spaceKey: any;
}> = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-TemplatesPage" */ './pages/look-and-feel/templates/TemplatesAndBlueprintsPage.tsx'
			)
		).TemplatesAndBlueprintsPage,
	name: 'TemplatesPage',
});

export const SpaceSettingsSideNavigation: LoadableType<SpaceSettingsSideNavigationProps> =
	LoadableAfterPaint({
		loader: async () =>
			(
				await import(
					/* webpackChunkName: "loadable-SpaceSettingsSideNavigation" */ './side-navigation/SpaceSettingsSideNavigation.tsx'
				)
			).SpaceSettingsSideNavigation,
		name: 'SpaceSettingsSideNavigation',
	});

export const LazySpaceDetailsPage: LoadableType<{
	spaceKey: string;
}> = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-SpaceDetailsPage" */ './pages/SpaceDetails/SpaceDetailsPage.tsx'
			)
		).SpaceDetailsPage,
	name: 'SpaceDetailsPage',
});

export { SPACE_SETTINGS_DETAILS_ANALYTICS_SOURCE } from './pages/SpaceDetails/spaceDetailsConstants.ts';

export const LazyPdfExportVersionSwitcher: LoadableType<{
	spaceKey: string;
}> = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-PdfExportVersionSwitcher" */ './pages/look-and-feel/pdf-export/PdfExportVersionSwitcher.tsx'
			)
		).PdfExportVersionSwitcher,
	name: 'PdfExportVersionSwitcher',
});
